import React, { useState, useEffect } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  TextField,
  MenuItem,
  Button,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Menu,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Autocomplete,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  FileDownload as ExcelIcon,
  PictureAsPdf as PdfIcon,
  Print as PrintIcon,
  ViewColumn as ColumnsIcon,
  Search as SearchIcon,
  SwapHoriz,
  Inventory,
  Assessment,
  Speed,
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { WarehouseTransfer } from '../../types/warehouseTransfers.types';
import { warehouseTransfersService } from '../../services/warehouseTransfers.service';
import { Warehouse } from '../../types/warehouses.types';
import { warehousesService } from '../../services/warehouses.service';
import { companyInfoService, CompanyInfo } from '../../services/companyInfo.service';
import { toast } from 'react-toastify';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useFiscalDateRange } from '../../hooks/useFiscalDateRange';

const translations = {
  en: {
    title: 'Warehouse Transfers Report',
    titleAr: 'تقرير نقل المخزون بين المخازن',
    fromWarehouse: 'From Warehouse',
    toWarehouse: 'To Warehouse',
    allWarehouses: 'All Warehouses',
    allStatuses: 'All Statuses',
    status: 'Status',
    dateFrom: 'From Date',
    dateTo: 'To Date',
    search: 'Search...',
    today: 'Today',
    thisWeek: 'This Week',
    thisMonth: 'This Month',
    lastMonth: 'Last Month',
    thisYear: 'This Year',
    detailedView: 'Detailed Transfers',
    summaryByFromWarehouse: 'Summary by Source',
    summaryByToWarehouse: 'Summary by Destination',
    summaryByItem: 'Summary by Item',
    transferNumber: 'Transfer #',
    transferDate: 'Date',
    fromWarehouseName: 'From Warehouse',
    toWarehouseName: 'To Warehouse',
    itemCode: 'Item Code',
    itemName: 'Item',
    unitName: 'Unit',
    requestedQty: 'Requested',
    shippedQty: 'Shipped',
    receivedQty: 'Received',
    unitCost: 'Unit Cost',
    totalCost: 'Total',
    notes: 'Notes',
    totalTransfers: 'Total Transfers',
    totalQuantity: 'Total Qty',
    totalValue: 'Total Value',
    avgTransferAmount: 'Avg Amount',
    draft: 'Draft',
    inTransit: 'In Transit',
    received: 'Received',
    posted: 'Posted',
    stats: 'Statistics',
    transfersCount: 'Total Transfers',
    itemsTransferred: 'Items Transferred',
    refresh: 'Refresh',
    exportExcel: 'Export Excel',
    exportPdf: 'Export PDF',
    print: 'Print',
    columns: 'Columns',
    filters: 'Filters',
    noData: 'No warehouse transfers found',
    loading: 'Loading...',
    kwd: 'KWD',
  },
  ar: {
    title: 'تقرير نقل المخزون بين المخازن',
    titleAr: 'تقرير نقل المخزون بين المخازن',
    fromWarehouse: 'من مخزن',
    toWarehouse: 'إلى مخزن',
    allWarehouses: 'جميع المخازن',
    allStatuses: 'جميع الحالات',
    status: 'الحالة',
    dateFrom: 'من تاريخ',
    dateTo: 'إلى تاريخ',
    search: 'بحث...',
    today: 'اليوم',
    thisWeek: 'هذا الأسبوع',
    thisMonth: 'هذا الشهر',
    lastMonth: 'الشهر الماضي',
    thisYear: 'هذه السنة',
    detailedView: 'التحويلات التفصيلية',
    summaryByFromWarehouse: 'ملخص حسب المخزن المصدر',
    summaryByToWarehouse: 'ملخص حسب المخزن الوجهة',
    summaryByItem: 'ملخص حسب الصنف',
    transferNumber: 'رقم التحويل',
    transferDate: 'التاريخ',
    fromWarehouseName: 'من مخزن',
    toWarehouseName: 'إلى مخزن',
    itemCode: 'كود الصنف',
    itemName: 'الصنف',
    unitName: 'الوحدة',
    requestedQty: 'المطلوب',
    shippedQty: 'المشحون',
    receivedQty: 'المستلم',
    unitCost: 'سعر الوحدة',
    totalCost: 'الإجمالي',
    notes: 'ملاحظات',
    totalTransfers: 'إجمالي التحويلات',
    totalQuantity: 'إجمالي الكمية',
    totalValue: 'القيمة الإجمالية',
    avgTransferAmount: 'متوسط المبلغ',
    draft: 'مسودة',
    inTransit: 'قيد النقل',
    received: 'مستلم',
    posted: 'مرحّل',
    stats: 'الإحصائيات',
    transfersCount: 'عدد التحويلات',
    itemsTransferred: 'الأصناف المحولة',
    refresh: 'تحديث',
    exportExcel: 'تصدير Excel',
    exportPdf: 'تصدير PDF',
    print: 'طباعة',
    columns: 'الأعمدة',
    filters: 'الفلاتر',
    noData: 'لا توجد تحويلات مخزون',
    loading: 'جاري التحميل...',
    kwd: 'د.ك',
  },
};

interface DetailedTransfer {
  transferNumber: string;
  transferDate: string;
  fromWarehouseId: number;
  fromWarehouseName: string;
  toWarehouseId: number;
  toWarehouseName: string;
  itemCode: string;
  itemName: string;
  itemNameAr: string;
  unitName: string;
  unitNameAr: string;
  requestedQuantity: number;
  shippedQuantity?: number;
  receivedQuantity?: number;
  unitCost: number;
  totalCost: number;
  status: string;
}

interface WarehouseSummary {
  warehouseId: number;
  warehouseName: string;
  warehouseNameAr: string;
  transfersCount: number;
  totalQuantity: number;
  totalValue: number;
}

interface ItemSummary {
  itemId: number;
  itemCode: string;
  itemName: string;
  itemNameAr: string;
  transfersCount: number;
  totalQuantity: number;
  avgCost: number;
  totalValue: number;
}

const WarehouseTransfersReport: React.FC = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const isRTL = language === 'ar';
  const { minDate, maxDate } = useFiscalDateRange();

  const [transfers, setTransfers] = useState<WarehouseTransfer[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  
  const [fromWarehouseFilter, setFromWarehouseFilter] = useState<number | ''>('');
  const [toWarehouseFilter, setToWarehouseFilter] = useState<number | ''>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [dateFromFilter, setDateFromFilter] = useState<string>('');
  const [dateToFilter, setDateToFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [datePreset, setDatePreset] = useState<string>('');
  const [detailPage, setDetailPage] = useState(0);
  const [detailRowsPerPage, setDetailRowsPerPage] = useState(25);
  
  const [columnsAnchor, setColumnsAnchor] = useState<null | HTMLElement>(null);
  const [visibleColumns, setVisibleColumns] = useState({
    transferNumber: true,
    date: true,
    fromWarehouse: true,
    toWarehouse: true,
    item: true,
    requested: true,
    shipped: true,
    received: true,
    cost: true,
  });

  useEffect(() => {
    loadWarehouses();
    loadCompanyInfo();
  }, []);

  useEffect(() => {
    loadData();
    setDetailPage(0);
  }, [fromWarehouseFilter, toWarehouseFilter, statusFilter, dateFromFilter, dateToFilter]);

  const loadCompanyInfo = async () => {
    try {
      const info = await companyInfoService.getCompanyInfo();
      setCompanyInfo(info);
    } catch (error) {
      console.error('Failed to load company info:', error);
    }
  };

  // ✅ تعديل: فلترة المخازن من JWT بدلاً من إظهار الكل
  const loadWarehouses = async () => {
    try {
      const data = await warehousesService.getAll();

      const token = localStorage.getItem('token');
      let userBranchIds: number[] = [];
      let userWarehouseIds: number[] = [];

      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const primaryBranch = payload.BranchId ? [Number(payload.BranchId)] : [];
          const allowedBranches = payload.AllowedBranchId
            ? (Array.isArray(payload.AllowedBranchId) ? payload.AllowedBranchId.map(Number) : [Number(payload.AllowedBranchId)])
            : [];
          userBranchIds = Array.from(new Set([...primaryBranch, ...allowedBranches]));

          const keeperClaims = payload.KeeperWarehouseId;
          userWarehouseIds = Array.isArray(keeperClaims) ? keeperClaims.map(Number) : keeperClaims ? [Number(keeperClaims)] : [];
        } catch (e) { console.error('Failed to parse token:', e); }
      }

      const filteredWarehouses = userWarehouseIds.length > 0
        ? data.filter((w: Warehouse) => userWarehouseIds.includes(w.warehouseId))
        : userBranchIds.length === 0
          ? data
          : data.filter((w: Warehouse) => w.branchId && userBranchIds.includes(w.branchId));

      setWarehouses(filteredWarehouses);
    } catch (error) {
      console.error('Failed to load warehouses:', error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await warehouseTransfersService.getAll(
        statusFilter || undefined,
        fromWarehouseFilter || undefined,
        toWarehouseFilter || undefined,
        dateFromFilter || undefined,
        dateToFilter || undefined
      );
      setTransfers(data);
    } catch (error) {
      console.error('Failed to load warehouse transfers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDatePreset = (preset: string) => {
    setDatePreset(preset);
    const today = new Date();
    let fromDate = '';
    let toDate = today.toISOString().split('T')[0];

    switch(preset) {
      case 'today':
        fromDate = toDate;
        break;
      case 'thisWeek':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        fromDate = weekStart.toISOString().split('T')[0];
        break;
      case 'thisMonth':
        fromDate = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        break;
      case 'lastMonth':
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        fromDate = lastMonth.toISOString().split('T')[0];
        toDate = new Date(today.getFullYear(), today.getMonth(), 0).toISOString().split('T')[0];
        break;
      case 'thisYear':
        fromDate = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0];
        break;
    }

    setDateFromFilter(fromDate);
    setDateToFilter(toDate);
  };

  const getDetailedData = (): DetailedTransfer[] => {
    const detailed: DetailedTransfer[] = [];
    
    transfers.forEach(transfer => {
      transfer.details.forEach(detail => {
        detailed.push({
          transferNumber: transfer.transferNumber,
          transferDate: transfer.transferDate,
          fromWarehouseId: transfer.fromWarehouseId,
          fromWarehouseName: isRTL ? (transfer.fromWarehouseNameAr || transfer.fromWarehouseName || '-') : (transfer.fromWarehouseName || '-'),
          toWarehouseId: transfer.toWarehouseId,
          toWarehouseName: isRTL ? (transfer.toWarehouseNameAr || transfer.toWarehouseName || '-') : (transfer.toWarehouseName || '-'),
          itemCode: detail.itemCode,
          itemName: detail.itemName,
          itemNameAr: detail.itemNameAr,
          unitName: detail.unitName,
          unitNameAr: detail.unitNameAr,
          requestedQuantity: detail.requestedQuantity,
          shippedQuantity: detail.shippedQuantity,
          receivedQuantity: detail.receivedQuantity,
          unitCost: detail.unitCost,
          totalCost: detail.unitCost * (detail.receivedQuantity || detail.shippedQuantity || detail.requestedQuantity),
          status: transfer.status || 'Draft',
        });
      });
    });

    return detailed;
  };

  const getFilteredData = () => {
    let filtered = getDetailedData();
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r =>
        r.transferNumber.toLowerCase().includes(query) ||
        r.fromWarehouseName.toLowerCase().includes(query) ||
        r.toWarehouseName.toLowerCase().includes(query) ||
        r.itemName.toLowerCase().includes(query) ||
        r.itemCode.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  };

  const getSummaryByFromWarehouse = (): WarehouseSummary[] => {
    const summary = new Map<number, WarehouseSummary>();
    
    transfers.forEach(transfer => {
      if (!summary.has(transfer.fromWarehouseId)) {
        summary.set(transfer.fromWarehouseId, {
          warehouseId: transfer.fromWarehouseId,
          warehouseName: transfer.fromWarehouseName || '-',
          warehouseNameAr: transfer.fromWarehouseNameAr || '-',
          transfersCount: 0,
          totalQuantity: 0,
          totalValue: 0,
        });
      }
      
      const wh = summary.get(transfer.fromWarehouseId)!;
      wh.transfersCount++;
      transfer.details.forEach(d => {
        wh.totalQuantity += (d.receivedQuantity || d.shippedQuantity || d.requestedQuantity);
        wh.totalValue += d.unitCost * (d.receivedQuantity || d.shippedQuantity || d.requestedQuantity);
      });
    });
    
    return Array.from(summary.values()).sort((a, b) => b.totalValue - a.totalValue);
  };

  const getSummaryByToWarehouse = (): WarehouseSummary[] => {
    const summary = new Map<number, WarehouseSummary>();
    
    transfers.forEach(transfer => {
      if (!summary.has(transfer.toWarehouseId)) {
        summary.set(transfer.toWarehouseId, {
          warehouseId: transfer.toWarehouseId,
          warehouseName: transfer.toWarehouseName || '-',
          warehouseNameAr: transfer.toWarehouseNameAr || '-',
          transfersCount: 0,
          totalQuantity: 0,
          totalValue: 0,
        });
      }
      
      const wh = summary.get(transfer.toWarehouseId)!;
      wh.transfersCount++;
      transfer.details.forEach(d => {
        wh.totalQuantity += (d.receivedQuantity || d.shippedQuantity || d.requestedQuantity);
        wh.totalValue += d.unitCost * (d.receivedQuantity || d.shippedQuantity || d.requestedQuantity);
      });
    });
    
    return Array.from(summary.values()).sort((a, b) => b.totalValue - a.totalValue);
  };

  const getSummaryByItem = (): ItemSummary[] => {
    const summary = new Map<number, ItemSummary>();
    
    transfers.forEach(transfer => {
      transfer.details.forEach(detail => {
        if (!summary.has(detail.itemId)) {
          summary.set(detail.itemId, {
            itemId: detail.itemId,
            itemCode: detail.itemCode,
            itemName: detail.itemName,
            itemNameAr: detail.itemNameAr,
            transfersCount: 0,
            totalQuantity: 0,
            avgCost: 0,
            totalValue: 0,
          });
        }
        
        const item = summary.get(detail.itemId)!;
        item.transfersCount++;
        const qty = detail.receivedQuantity || detail.shippedQuantity || detail.requestedQuantity;
        item.totalQuantity += qty;
        item.totalValue += detail.unitCost * qty;
      });
    });
    
    summary.forEach(item => {
      item.avgCost = item.totalQuantity > 0 ? item.totalValue / item.totalQuantity : 0;
    });
    
    return Array.from(summary.values()).sort((a, b) => b.totalQuantity - a.totalQuantity);
  };

  const getStatistics = () => {
    const detailed = getDetailedData();
    const uniqueItems = new Set(detailed.map(d => d.itemCode)).size;
    const totalValue = detailed.reduce((sum, d) => sum + d.totalCost, 0);
    
    return {
      transfersCount: transfers.length,
      itemsTransferred: uniqueItems,
      totalValue: totalValue,
      avgValue: transfers.length > 0 ? totalValue / transfers.length : 0,
    };
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB');
  };

  const formatCurrency = (amount: number) => {
    return amount.toFixed(3);
  };

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'Posted': return 'success';
      case 'Received': return 'info';
      case 'InTransit': return 'warning';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch(status) {
      case 'Posted': return t.posted;
      case 'Received': return t.received;
      case 'InTransit': return t.inTransit;
      default: return t.draft;
    }
  };

  const exportToExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(t.title);

      if (companyInfo?.logoBase64) {
        try {
          const logoImage = workbook.addImage({
            base64: companyInfo.logoBase64,
            extension: 'png',
          });
          worksheet.addImage(logoImage, {
            tl: { col: 0, row: 0 },
            ext: { width: 100, height: 80 }
          });
        } catch (err) {
          console.warn('Could not add logo:', err);
        }
      }

      worksheet.mergeCells('C1:J1');
      const companyCell = worksheet.getCell('C1');
      companyCell.value = isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName;
      companyCell.font = { size: 20, bold: true, color: { argb: 'FF1565C0' } };
      companyCell.alignment = { horizontal: 'center', vertical: 'middle' };

      worksheet.mergeCells('C2:J2');
      const addressCell = worksheet.getCell('C2');
      addressCell.value = companyInfo?.address || '';
      addressCell.font = { size: 10 };
      addressCell.alignment = { horizontal: 'center' };

      worksheet.mergeCells('C3:J3');
      const contactCell = worksheet.getCell('C3');
      contactCell.value = `${t.kwd} | ${companyInfo?.phone || ''} | ${companyInfo?.email || ''}`;
      contactCell.font = { size: 9 };
      contactCell.alignment = { horizontal: 'center' };

      worksheet.getRow(4).height = 10;

      worksheet.mergeCells('A5:M5');
      const titleCell = worksheet.getCell('A5');
      titleCell.value = isRTL ? t.titleAr : t.title;
      titleCell.font = { size: 16, bold: true, color: { argb: 'FFFFFFFF' } };
      titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
      titleCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF1565C0' }
      };

      const tabNames = [t.detailedView, t.summaryByFromWarehouse, t.summaryByToWarehouse, t.summaryByItem];
      worksheet.mergeCells('A6:M6');
      const tabCell = worksheet.getCell('A6');
      tabCell.value = `📊 ${tabNames[currentTab]}`;
      tabCell.font = { size: 12, bold: true, color: { argb: 'FF1565C0' } };
      tabCell.alignment = { horizontal: 'center' };

      worksheet.getRow(7).height = 10;

      if (currentTab === 0) {
        const data = getFilteredData();
        
        const headers = [
          t.transferNumber, t.transferDate, t.fromWarehouseName, t.toWarehouseName,
          t.itemCode, t.itemName, t.unitName,
          t.requestedQty, t.shippedQty, t.receivedQty, t.unitCost, t.totalCost, t.status
        ];
        
        const headerRow = worksheet.getRow(8);
        headers.forEach((header, i) => {
          const cell = headerRow.getCell(i + 1);
          cell.value = header;
          cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FF1565C0' }
          };
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
        });
        
        let currentRow = 9;
        data.forEach((item, index) => {
          const row = worksheet.getRow(currentRow);
          row.getCell(1).value = item.transferNumber;
          row.getCell(2).value = formatDate(item.transferDate);
          row.getCell(3).value = item.fromWarehouseName;
          row.getCell(4).value = item.toWarehouseName;
          row.getCell(5).value = item.itemCode;
          row.getCell(6).value = isRTL ? item.itemNameAr : item.itemName;
          row.getCell(7).value = isRTL ? item.unitNameAr : item.unitName;
          row.getCell(8).value = item.requestedQuantity;
          row.getCell(9).value = item.shippedQuantity || 0;
          row.getCell(10).value = item.receivedQuantity || 0;
          row.getCell(11).value = item.unitCost;
          row.getCell(12).value = item.totalCost;
          row.getCell(13).value = getStatusLabel(item.status);
          
          if (index % 2 === 0) {
            row.eachCell(cell => {
              cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFF5F5F5' }
              };
            });
          }
          
          currentRow++;
        });
        
        worksheet.columns = [
          { width: 16 }, { width: 14 }, { width: 20 }, { width: 20 },
          { width: 14 }, { width: 30 }, { width: 12 },
          { width: 12 }, { width: 12 }, { width: 12 }, { width: 12 }, { width: 12 }, { width: 12 }
        ];
      }

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `WarehouseTransfers_${new Date().getTime()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success(isRTL ? 'تم التصدير بنجاح ✅' : 'Exported successfully ✅');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error(isRTL ? 'فشل التصدير ❌' : 'Export failed ❌');
    }
  };

  const exportToPdf = async () => {
    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pageWidth = doc.internal.pageSize.width;

      doc.setFillColor(245, 250, 255);
      doc.rect(0, 0, pageWidth, 45, 'F');

      if (companyInfo?.logoBase64) {
        try {
          doc.addImage(companyInfo.logoBase64, 'PNG', 12, 8, 30, 25);
        } catch (err) {
          console.warn('Could not add logo:', err);
        }
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.setTextColor(21, 101, 192);
      doc.text(isRTL ? (companyInfo?.companyNameAr || '') : (companyInfo?.companyName || ''), pageWidth / 2, 15, { align: 'center' });

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.text(companyInfo?.address || '', pageWidth / 2, 22, { align: 'center' });
      doc.text(`${companyInfo?.phone || ''} | ${companyInfo?.email || ''}`, pageWidth / 2, 27, { align: 'center' });

      doc.setFillColor(21, 101, 192);
      doc.roundedRect(pageWidth / 2 - 60, 32, 120, 10, 3, 3, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(255, 255, 255);
      doc.text(isRTL ? t.titleAr : t.title, pageWidth / 2, 38.5, { align: 'center' });

      doc.setFontSize(9);
      doc.setTextColor(21, 101, 192);
      doc.text(new Date().toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB'), pageWidth - 15, 38.5, { align: 'right' });

      const tableHeaders = [
        t.transferDate,
        t.transferNumber,
        t.fromWarehouseName,
        t.toWarehouseName,
        t.itemName,
        t.requestedQty,
        t.receivedQty,
      ];

      const tableData: any[] = [];
      const data = getFilteredData();

      data.forEach((item) => {
        tableData.push([
          formatDate(item.transferDate),
          item.transferNumber,
          item.fromWarehouseName.substring(0, 15),
          item.toWarehouseName.substring(0, 15),
          (isRTL ? item.itemNameAr : item.itemName).substring(0, 20),
          formatCurrency(item.requestedQuantity),
          formatCurrency(item.receivedQuantity || 0),
        ]);
      });

      const stats = getStatistics();
      tableData.push([
        {
          content: 'TOTAL',
          colSpan: 5,
          styles: {
            fillColor: [13, 71, 161],
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            fontSize: 11,
            halign: 'center'
          }
        },
        {
          content: '',
          styles: { fillColor: [13, 71, 161] }
        },
        {
          content: formatCurrency(stats.totalValue),
          styles: { fillColor: [13, 71, 161], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 11, halign: 'center' }
        }
      ]);

      autoTable(doc, {
        head: [tableHeaders],
        body: tableData,
        startY: 50,
        theme: 'striped',
        headStyles: {
          fillColor: [21, 101, 192],
          textColor: [255, 255, 255],
          fontSize: 9,
          fontStyle: 'bold',
          halign: 'center'
        },
        bodyStyles: {
          fontSize: 8,
          textColor: [50, 50, 50]
        },
        alternateRowStyles: {
          fillColor: [248, 250, 251]
        },
      });

      doc.save(`WarehouseTransfers_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success(isRTL ? 'تم التصدير بنجاح ✅' : 'Exported successfully ✅');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error(isRTL ? 'فشل التصدير ❌' : 'Export failed ❌');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const stats = getStatistics();

  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ 
          background: 'linear-gradient(45deg, #1565C0 30%, #1976D2 90%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          fontWeight: 'bold'
        }}>
          {t.title}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1 }} className="no-print">
          <Tooltip title={t.refresh}>
            <IconButton onClick={loadData} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t.columns}>
            <IconButton onClick={(e) => setColumnsAnchor(e.currentTarget)} color="primary">
              <ColumnsIcon />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<ExcelIcon />} onClick={exportToExcel}
            sx={{ background: 'linear-gradient(45deg, #4CAF50 30%, #66BB6A 90%)', color: 'white' }}>
            {t.exportExcel}
          </Button>
          <Button variant="contained" startIcon={<PdfIcon />} onClick={exportToPdf}
            sx={{ background: 'linear-gradient(45deg, #f44336 30%, #e57373 90%)', color: 'white' }}>
            {t.exportPdf}
          </Button>
          <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint}
            sx={{ background: 'linear-gradient(45deg, #FF9800 30%, #FFB74D 90%)', color: 'white' }}>
            {t.print}
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }} className="no-print">
        <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{t.transfersCount}</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>{stats.transfersCount}</Typography>
              </Box>
              <SwapHoriz sx={{ fontSize: 48, opacity: 0.3 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{t.itemsTransferred}</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>{stats.itemsTransferred}</Typography>
              </Box>
              <Inventory sx={{ fontSize: 48, opacity: 0.3 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{t.totalValue}</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>{formatCurrency(stats.totalValue)}</Typography>
              </Box>
              <Assessment sx={{ fontSize: 48, opacity: 0.3 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{t.avgTransferAmount}</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>{formatCurrency(stats.avgValue)}</Typography>
              </Box>
              <Speed sx={{ fontSize: 48, opacity: 0.3 }} />
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }} className="no-print">
        <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>{t.filters}</Typography>
        
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          {['today', 'thisWeek', 'thisMonth', 'lastMonth', 'thisYear'].map((preset) => (
            <Chip key={preset} label={t[preset as keyof typeof t]} onClick={() => handleDatePreset(preset)}
              color={datePreset === preset ? 'primary' : 'default'} variant={datePreset === preset ? 'filled' : 'outlined'} />
          ))}
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' }, gap: 2 }}>
          <Autocomplete
            size="small"
            options={[{ id: '' as number | '', label: t.allWarehouses }, ...warehouses.map(wh => ({ id: wh.warehouseId as number | '', label: isRTL ? wh.warehouseNameAr || wh.warehouseName : wh.warehouseName }))]}
            getOptionLabel={(o) => o.label || ''}
            value={[{ id: '' as number | '', label: t.allWarehouses }, ...warehouses.map(wh => ({ id: wh.warehouseId as number | '', label: isRTL ? wh.warehouseNameAr || wh.warehouseName : wh.warehouseName }))].find(o => o.id === fromWarehouseFilter) || { id: '' as number | '', label: t.allWarehouses }}
            onChange={(_, v) => setFromWarehouseFilter(v?.id || '')}
            renderInput={(params) => <TextField {...params} label={t.fromWarehouse} />}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            fullWidth
          />

          <Autocomplete
            size="small"
            options={[{ id: '' as number | '', label: t.allWarehouses }, ...warehouses.map(wh => ({ id: wh.warehouseId as number | '', label: isRTL ? wh.warehouseNameAr || wh.warehouseName : wh.warehouseName }))]}
            getOptionLabel={(o) => o.label || ''}
            value={[{ id: '' as number | '', label: t.allWarehouses }, ...warehouses.map(wh => ({ id: wh.warehouseId as number | '', label: isRTL ? wh.warehouseNameAr || wh.warehouseName : wh.warehouseName }))].find(o => o.id === toWarehouseFilter) || { id: '' as number | '', label: t.allWarehouses }}
            onChange={(_, v) => setToWarehouseFilter(v?.id || '')}
            renderInput={(params) => <TextField {...params} label={t.toWarehouse} />}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            fullWidth
          />

          <TextField select fullWidth size="small" label={t.status} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="">{t.allStatuses}</MenuItem>
            <MenuItem value="Draft">{t.draft}</MenuItem>
            <MenuItem value="InTransit">{t.inTransit}</MenuItem>
            <MenuItem value="Received">{t.received}</MenuItem>
            <MenuItem value="Posted">{t.posted}</MenuItem>
          </TextField>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t.dateFrom}
              value={dateFromFilter ? new Date(dateFromFilter) : null}
              onChange={(date) => {
                const newValue = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                setDateFromFilter(newValue);
                if (dateToFilter && newValue && dateToFilter < newValue) setDateToFilter('');
              }}
              format="dd/MM/yyyy"
              views={['year', 'month', 'day']}
              openTo="day"
              minDate={minDate}
              maxDate={dateToFilter ? new Date(dateToFilter) : maxDate}
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
            />
          </LocalizationProvider>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t.dateTo}
              value={dateToFilter ? new Date(dateToFilter) : null}
              onChange={(date) => {
                const newValue = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                setDateToFilter(newValue);
                if (dateFromFilter && newValue && dateFromFilter > newValue) setDateFromFilter('');
              }}
              format="dd/MM/yyyy"
              views={['year', 'month', 'day']}
              openTo="day"
              minDate={dateFromFilter ? new Date(dateFromFilter) : minDate}
              maxDate={maxDate}
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
            />
          </LocalizationProvider>
        </Box>

        <TextField fullWidth size="small" placeholder={t.search} value={searchQuery} onChange={(e) => { setSearchQuery(e.target.value); setDetailPage(0); }} sx={{ mt: 2 }}
          InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }} />
      </Paper>

      <Paper sx={{ mb: 2 }} className="no-print">
        <Tabs value={currentTab} onChange={(_, newValue) => setCurrentTab(newValue)} variant="scrollable" scrollButtons="auto">
          <Tab label={t.detailedView} />
          <Tab label={t.summaryByFromWarehouse} />
          <Tab label={t.summaryByToWarehouse} />
          <Tab label={t.summaryByItem} />
        </Tabs>
      </Paper>

      <Box className="print-only">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, pb: 1, borderBottom: '2px solid #1565C0' }}>
          {companyInfo?.logoBase64 && (
            <Box><img src={companyInfo.logoBase64} alt="Logo" style={{ height: '40px', maxWidth: '100px' }} /></Box>
          )}
          <Box sx={{ textAlign: 'center', flex: 1 }}>
            <Typography sx={{ fontSize: '20px', fontWeight: 'bold', color: '#1565C0', mb: 0.3 }}>
              {isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName}
            </Typography>
            <Typography sx={{ fontSize: '9px', color: '#546E7A' }}>{companyInfo?.address || ''} | 📞 {companyInfo?.phone || ''}</Typography>
          </Box>
          <Box sx={{ width: '100px' }}></Box>
        </Box>

        <Box sx={{ textAlign: 'center', mb: 1, py: 0.5, background: 'linear-gradient(90deg, #1565C0 0%, #1976D2 100%)', color: 'white' }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 'bold' }}>{isRTL ? t.titleAr : t.title} - {[t.detailedView, t.summaryByFromWarehouse, t.summaryByToWarehouse, t.summaryByItem][currentTab]}</Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, fontSize: '8px', color: '#546E7A' }}>
          <Typography sx={{ fontSize: '8px' }}>
            {new Date().toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB', { year: 'numeric', month: 'short', day: 'numeric' })}
          </Typography>
          <Typography sx={{ fontSize: '8px' }}>{new Date().toLocaleTimeString(isRTL ? 'ar-KW' : 'en-GB')}</Typography>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
          {currentTab === 0 && (
            <>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow sx={{ background: 'linear-gradient(45deg, #1565C0 30%, #1976D2 90%)' }}>
                    {visibleColumns.transferNumber && <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.transferNumber}</TableCell>}
                    {visibleColumns.date && <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.transferDate}</TableCell>}
                    {visibleColumns.fromWarehouse && <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.fromWarehouseName}</TableCell>}
                    {visibleColumns.toWarehouse && <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.toWarehouseName}</TableCell>}
                    {visibleColumns.item && <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.itemName}</TableCell>}
                    {visibleColumns.requested && <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.requestedQty}</TableCell>}
                    {visibleColumns.shipped && <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.shippedQty}</TableCell>}
                    {visibleColumns.received && <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.receivedQty}</TableCell>}
                    {visibleColumns.cost && <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.totalCost}</TableCell>}
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.status}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getFilteredData()
                    .slice(detailPage * detailRowsPerPage, detailPage * detailRowsPerPage + detailRowsPerPage)
                    .map((row, index) => (
                      <TableRow key={index} sx={{ '&:hover': { backgroundColor: 'action.hover' }, backgroundColor: index % 2 === 0 ? 'background.default' : 'action.hover' }}>
                        {visibleColumns.transferNumber && <TableCell>{row.transferNumber}</TableCell>}
                        {visibleColumns.date && <TableCell>{formatDate(row.transferDate)}</TableCell>}
                        {visibleColumns.fromWarehouse && <TableCell>{row.fromWarehouseName}</TableCell>}
                        {visibleColumns.toWarehouse && <TableCell>{row.toWarehouseName}</TableCell>}
                        {visibleColumns.item && (
                          <TableCell>
                            <Typography variant="body2" fontWeight="500">{isRTL ? row.itemNameAr : row.itemName}</Typography>
                            <Typography variant="caption" color="text.secondary">{row.itemCode}</Typography>
                          </TableCell>
                        )}
                        {visibleColumns.requested && <TableCell align="right">{formatCurrency(row.requestedQuantity)}</TableCell>}
                        {visibleColumns.shipped && <TableCell align="right">{formatCurrency(row.shippedQuantity || 0)}</TableCell>}
                        {visibleColumns.received && <TableCell align="right">{formatCurrency(row.receivedQuantity || 0)}</TableCell>}
                        {visibleColumns.cost && <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(row.totalCost)}</TableCell>}
                        <TableCell>
                          <Chip label={getStatusLabel(row.status)} size="small" color={getStatusColor(row.status)} />
                        </TableCell>
                      </TableRow>
                    ))}
                  {getFilteredData().length === 0 && (
                    <TableRow><TableCell colSpan={10} align="center" sx={{ py: 5 }}><Typography color="text.secondary">{t.noData}</Typography></TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={getFilteredData().length}
                page={detailPage}
                onPageChange={(_, p) => setDetailPage(p)}
                rowsPerPage={detailRowsPerPage}
                onRowsPerPageChange={(e) => { setDetailRowsPerPage(parseInt(e.target.value, 10)); setDetailPage(0); }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                className="no-print"
              />
            </>
          )}

          {currentTab === 1 && (
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ background: 'linear-gradient(45deg, #1565C0 30%, #1976D2 90%)' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.fromWarehouseName}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.transfersCount}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.totalQuantity}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.totalValue}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getSummaryByFromWarehouse().map((row, index) => (
                  <TableRow key={index} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                    <TableCell sx={{ fontWeight: '500' }}>{isRTL ? row.warehouseNameAr : row.warehouseName}</TableCell>
                    <TableCell align="right">{row.transfersCount}</TableCell>
                    <TableCell align="right">{formatCurrency(row.totalQuantity)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main' }}>{formatCurrency(row.totalValue)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {currentTab === 2 && (
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ background: 'linear-gradient(45deg, #1565C0 30%, #1976D2 90%)' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.toWarehouseName}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.transfersCount}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.totalQuantity}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.totalValue}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getSummaryByToWarehouse().map((row, index) => (
                  <TableRow key={index} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                    <TableCell sx={{ fontWeight: '500' }}>{isRTL ? row.warehouseNameAr : row.warehouseName}</TableCell>
                    <TableCell align="right">{row.transfersCount}</TableCell>
                    <TableCell align="right">{formatCurrency(row.totalQuantity)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main' }}>{formatCurrency(row.totalValue)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {currentTab === 3 && (
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ background: 'linear-gradient(45deg, #1565C0 30%, #1976D2 90%)' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.itemName}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.transfersCount}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.totalQuantity}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.avgTransferAmount}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.totalValue}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getSummaryByItem().map((row, index) => (
                  <TableRow key={index} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="500">{isRTL ? row.itemNameAr : row.itemName}</Typography>
                      <Typography variant="caption" color="text.secondary">{row.itemCode}</Typography>
                    </TableCell>
                    <TableCell align="right">{row.transfersCount}</TableCell>
                    <TableCell align="right">{formatCurrency(row.totalQuantity)}</TableCell>
                    <TableCell align="right">{formatCurrency(row.avgCost)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main' }}>{formatCurrency(row.totalValue)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      )}

      <Menu anchorEl={columnsAnchor} open={Boolean(columnsAnchor)} onClose={() => setColumnsAnchor(null)}>
        {Object.keys(visibleColumns).map((key) => (
          <MenuItem key={key}>
            <FormControlLabel control={<Checkbox checked={visibleColumns[key as keyof typeof visibleColumns]}
              onChange={(e) => setVisibleColumns({ ...visibleColumns, [key]: e.target.checked })} />} label={key} />
          </MenuItem>
        ))}
      </Menu>

      <Box className="print-footer">
        <Typography sx={{ fontSize: '8px', color: '#546E7A' }}>
          {isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName} - {isRTL ? t.titleAr : t.title}
          <span style={{ marginLeft: '20px', marginRight: '20px' }}>|</span>
          <span className="page-number"></span>
        </Typography>
      </Box>

      <style>{`
        @media print {
          header, nav, .MuiAppBar-root, .MuiDrawer-root, .MuiToolbar-root { display: none !important; }
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          @page { margin: 0.5cm 1cm 1.5cm 1cm; size: landscape; }
          * { orphans: 3; widows: 3; }
          table { page-break-after: avoid; }
          body { print-color-adjust: exact; -webkit-print-color-adjust: exact; font-size: 9px; }
          main, .MuiBox-root { margin: 0 !important; padding: 8px !important; max-width: 100% !important; }
          table thead tr { background: linear-gradient(135deg, #1565C0 0%, #1976D2 100%) !important; -webkit-print-color-adjust: exact; }
          table thead th { background: linear-gradient(135deg, #1565C0 0%, #1976D2 100%) !important; color: white !important; font-weight: bold !important; padding: 4px 6px !important; border: 1px solid #1565C0 !important; font-size: 9px !important; -webkit-print-color-adjust: exact; }
          table tbody tr:nth-child(odd) { background-color: #F5F9FF !important; -webkit-print-color-adjust: exact; }
          table tbody tr:nth-child(even) { background-color: #FFFFFF !important; -webkit-print-color-adjust: exact; }
          table tbody td { padding: 3px 6px !important; border: 1px solid #E3F2FD !important; color: #263238 !important; font-size: 8px !important; line-height: 1.2 !important; -webkit-print-color-adjust: exact; }
          table tbody td .MuiTypography-caption { color: #546E7A !important; opacity: 1 !important; }
          .MuiChip-root { height: 16px !important; font-size: 7px !important; -webkit-print-color-adjust: exact; }
          .MuiChip-colorSuccess { background-color: #4CAF50 !important; color: white !important; -webkit-print-color-adjust: exact; }
          .MuiChip-colorInfo { background-color: #2196F3 !important; color: white !important; -webkit-print-color-adjust: exact; }
          .MuiChip-colorWarning { background-color: #FF9800 !important; color: white !important; -webkit-print-color-adjust: exact; }
          .MuiTableContainer-root { box-shadow: none !important; border-radius: 0 !important; overflow: visible !important; }
          .print-only h4, .print-only h3, .print-only h5 { color: #1565C0 !important; -webkit-print-color-adjust: exact; }
          .print-only .MuiPaper-root { -webkit-print-color-adjust: exact; box-shadow: none !important; }
          .print-only img { -webkit-print-color-adjust: exact; }
          .print-footer { position: fixed; bottom: 0; left: 0; right: 0; text-align: center; font-size: 8px; padding: 5px; border-top: 1px solid #1565C0; background: white; }
        }
        @media screen { .print-only { display: none !important; } .print-footer { display: none !important; } }
      `}</style>
    </Box>
  );
};

export default WarehouseTransfersReport;